// VenuesList.js
import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogTitle, DialogDescription, DialogActions } from './dialog';
import { collection, getDocs, deleteDoc, doc, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { Text } from './text';
import { Heading } from './heading';
import clsx from 'clsx';
import { useNavigate, Navigate } from 'react-router-dom';
import Loader from './Loader';
import { useAuth } from './AuthProvider';
import { useVenue } from './VenueProvider';
import { showAlert } from '../alertService';
import {
  Bars3Icon,
  Squares2X2Icon,
  TableCellsIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  BuildingOffice2Icon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import { Dropdown, DropdownButton, DropdownMenu, DropdownItem } from './dropdown';
import { Badge } from './badge';

const ACCENT = '#1b4d2e';

// Mirrors backend _parse_basic_info — basicInfo has two historical shapes.
export function parseBasicInfo(basicInfo) {
  const empty = { capacity: null, type: null, priceRange: null, zone: null };
  if (!basicInfo) return empty;
  if (Array.isArray(basicInfo)) {
    const out = { ...empty };
    for (const item of basicInfo) {
      if (!item || typeof item !== 'object') continue;
      if (item.name === 'Capacidad') out.capacity = item.value;
      else if (item.name === 'Tipo') out.type = item.value;
      else if (item.name === 'Rango de precios') out.priceRange = item.value;
      else if (item.name === 'Zona') out.zone = item.value;
    }
    return out;
  }
  if (typeof basicInfo === 'object') {
    return {
      capacity: basicInfo['3'] ?? null,
      type: basicInfo['4'] ?? null,
      priceRange: basicInfo['5'] ?? null,
      zone: basicInfo['6'] ?? null,
    };
  }
  return empty;
}

const typesAsArray = (t) => (Array.isArray(t) ? t : t ? [t] : []);

// EN pendiente: el venue está completo solo cuando existen descriptionEn Y
// location.descriptionEn (string vacío o solo espacios = sin traducir).
// Si falta también el texto en español, sigue contando como pendiente: hay
// contenido por capturar en ambos idiomas.
// Los documentos cuentan igual, pero solo los que de verdad saldrían en
// español: si el nombre coincide con uno del catálogo (Reglamento, Planos...),
// la landing EN ya lo resuelve sola y no hay nada que capturar. `knownDocLabels`
// es el Set de nombres ES del catálogo `documentos`, en minúsculas.
const untranslatedDocs = (v, knownDocLabels) => {
  if (!Array.isArray(v.documentos)) return [];
  return v.documentos
    .filter((d) => {
      if (!d?.url || (d?.labelEn || '').trim()) return false;
      return !knownDocLabels?.has((d?.label || '').trim().toLowerCase());
    })
    .map((d) => (d?.label || '').trim() || 'Documento');
};

// Devuelve la lista de motivos por los que el venue está pendiente de EN.
// Vacía = está completo. Se usa para el badge y para el tooltip del indicador.
const enPendingReasons = (v, knownDocLabels) => {
  const reasons = [];
  if (!(v.descriptionEn || '').trim()) reasons.push('Descripción en inglés');
  if (!(v.location?.descriptionEn || '').trim())
    reasons.push('Descripción de ubicación en inglés');
  const docs = untranslatedDocs(v, knownDocLabels);
  if (docs.length) reasons.push(`Documentos sin nombre en inglés: ${docs.join(', ')}`);
  return reasons;
};

const needsEnTranslation = (v, knownDocLabels) =>
  enPendingReasons(v, knownDocLabels).length > 0;

const VenueCard = ({ venue, onDelete, isAdmin, knownDocLabels }) => {
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleClick = () => {
    navigate(`/dashboard/venues/${venue.id}`);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setShowDialog(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'venues', venue.id));
      onDelete(venue.id);
      setShowDialog(false);
      showAlert('Venue eliminado', 'success');
    } catch (error) {
      console.error('Error deleting venue:', error);
      showAlert('Error al eliminar el venue', 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={clsx(
        'relative rounded-2xl bg-white p-8 shadow-lg ring-1 ring-zinc-950/10',
        'dark:bg-zinc-900 dark:ring-white/10',
        'cursor-pointer hover:shadow-xl transition-shadow'
      )}
    >
      {isAdmin && (
        <div className="absolute top-2 right-2">
          <Dropdown>
            <DropdownButton onClick={(e) => e.stopPropagation()} outline>
              <Bars3Icon />
            </DropdownButton>
            <DropdownMenu>
              <DropdownItem onClick={handleDeleteClick}>Eliminar</DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      )}

      <img
        src={venue.images && venue.images.length ? venue.images[0] : '/icon_clear.png'}
        alt="Venue"
        className="h-48 w-auto mx-auto mb-4 rounded-lg object-cover"
      />
      <Heading level={3} className="text-center">
        {venue.name}
      </Heading>
      {venue.address && <Text className="mt-2 text-center">{venue.address}</Text>}
      {isAdmin && enPendingReasons(venue, knownDocLabels).length > 0 && (
        <div className="mt-2 text-center">
          <Badge
            color="amber"
            title={enPendingReasons(venue, knownDocLabels).join('\n')}
          >
            EN pendiente
          </Badge>
        </div>
      )}

      {showDialog && (
        <Dialog open={showDialog} onClose={() => setShowDialog(false)}>
          <DialogTitle>Eliminar venue</DialogTitle>
          <DialogDescription>
            ¿Seguro que quieres eliminar <strong>{venue.name}</strong>? Esta acción no se puede deshacer.
          </DialogDescription>
          <DialogActions>
            <button
              onClick={() => setShowDialog(false)}
              className="rounded bg-gray-300 px-4 py-2 text-gray-800 hover:bg-gray-400"
              disabled={deleting}
            >
              Cancelar
            </button>
            <button
              onClick={confirmDelete}
              className="rounded bg-red-500 px-4 py-2 text-white hover:bg-red-600"
              disabled={deleting}
            >
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

const StatusBadge = ({ active }) => (
  <Badge color={active ? 'green' : 'zinc'}>{active ? 'Activo' : 'Inactivo'}</Badge>
);

const VenueRow = ({ venue, isAdmin, onDelete, knownDocLabels }) => {
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const types = typesAsArray(venue.type);

  const confirmDelete = async (e) => {
    e?.stopPropagation();
    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'venues', venue.id));
      onDelete(venue.id);
      setShowDialog(false);
      showAlert('Venue eliminado', 'success');
    } catch (error) {
      console.error('Error deleting venue:', error);
      showAlert('Error al eliminar el venue', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const cover = venue.images && venue.images.length ? venue.images[0] : null;

  return (
    <>
      <tr
        onClick={() => navigate(`/dashboard/venues/${venue.id}`)}
        className="cursor-pointer hover:bg-zinc-50 transition-colors"
      >
        <td className="px-4 py-2.5">
          {cover ? (
            <img
              src={cover}
              alt=""
              className="h-10 w-14 rounded-md object-cover ring-1 ring-zinc-200"
            />
          ) : (
            <div className="h-10 w-14 rounded-md bg-zinc-100 flex items-center justify-center ring-1 ring-zinc-200">
              <BuildingOffice2Icon className="h-5 w-5 text-zinc-300" />
            </div>
          )}
        </td>
        <td className="px-4 py-2.5">
          <div className="font-medium text-zinc-900">{venue.name}</div>
          {venue.address && (
            <div className="text-xs text-zinc-500 truncate max-w-[26rem]">{venue.address}</div>
          )}
        </td>
        <td className="px-4 py-2.5 text-zinc-700">
          {venue.zone || <span className="text-zinc-300 italic">—</span>}
        </td>
        <td className="px-4 py-2.5 text-zinc-700">
          {types.length === 0 ? (
            <span className="text-zinc-300 italic">—</span>
          ) : (
            <span className="text-zinc-700">
              {types.slice(0, 2).join(', ')}
              {types.length > 2 && (
                <span className="text-zinc-400"> +{types.length - 2}</span>
              )}
            </span>
          )}
        </td>
        <td className="px-4 py-2.5 text-zinc-700">
          {venue.capacity || <span className="text-zinc-300 italic">—</span>}
        </td>
        <td className="px-4 py-2.5">
          <StatusBadge active={!!venue.isActive} />
        </td>
        {isAdmin && (
          <td className="px-4 py-2.5">
            <StatusBadge active={!!venue.isActiveAdmin} />
          </td>
        )}
        {isAdmin && (
          <td className="px-4 py-2.5">
            {(() => {
              // El tooltip lista exactamente qué falta, para no tener que abrir
              // el venue solo para averiguarlo.
              const reasons = enPendingReasons(venue, knownDocLabels);
              return reasons.length > 0 ? (
                <Badge color="amber" title={reasons.join('\n')}>
                  Pendiente
                </Badge>
              ) : (
                <Badge color="green">OK</Badge>
              );
            })()}
          </td>
        )}
        {isAdmin && (
          <td className="px-2 py-2.5 text-right">
            <div onClick={(e) => e.stopPropagation()}>
              <Dropdown>
                <DropdownButton plain aria-label="Acciones">
                  <Bars3Icon />
                </DropdownButton>
                <DropdownMenu anchor="bottom end">
                  <DropdownItem onClick={() => setShowDialog(true)}>Eliminar</DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </div>
          </td>
        )}
      </tr>
      {showDialog && (
        <Dialog open={showDialog} onClose={() => setShowDialog(false)}>
          <DialogTitle>Eliminar venue</DialogTitle>
          <DialogDescription>
            ¿Seguro que quieres eliminar <strong>{venue.name}</strong>? Esta acción no se puede deshacer.
          </DialogDescription>
          <DialogActions>
            <button
              onClick={() => setShowDialog(false)}
              className="rounded bg-gray-300 px-4 py-2 text-gray-800 hover:bg-gray-400"
              disabled={deleting}
            >
              Cancelar
            </button>
            <button
              onClick={confirmDelete}
              className="rounded bg-red-500 px-4 py-2 text-white hover:bg-red-600"
              disabled={deleting}
            >
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

const VenuesList = () => {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('table');

  const [search, setSearch] = useState('');
  const [filterZona, setFilterZona] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterPublic, setFilterPublic] = useState('');
  const [filterAdmin, setFilterAdmin] = useState('');
  const [filterEn, setFilterEn] = useState('');
  // Nombres ES del catálogo `documentos`: un documento con uno de estos nombres
  // ya se traduce solo en la landing EN, así que no cuenta como pendiente.
  const [knownDocLabels, setKnownDocLabels] = useState(() => new Set());

  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const isAdmin = userInfo?.role === 'admin';
  // Coherencia con el selector del sidebar: un owner nunca ve esta lista —
  // "Venue" es la ficha del venue seleccionado arriba (el nav ya manda ahí;
  // esto cubre deep-links y marcadores a /dashboard/venues). La lista con
  // filtros es de admins.
  const { selectedVenueId, venueLoading } = useVenue();

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'catalogos', 'documentos', 'items'));
        if (cancelled) return;
        const labels = new Set();
        snap.forEach((d) => {
          const es = (d.data()?.ES || '').trim().toLowerCase();
          if (es) labels.add(es);
        });
        setKnownDocLabels(labels);
      } catch (err) {
        // Sin catálogo el indicador solo se pone más estricto (marca pendientes
        // que quizá ya se resuelven solos); nunca deja de funcionar.
        console.error('No se pudo cargar el catálogo de documentos:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        let venuesData = [];
        const venuesCollection = collection(db, 'venues');

        if (userInfo.role === 'admin') {
          const querySnapshot = await getDocs(venuesCollection);
          venuesData = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        } else if (
          ['owner', 'viewer', 'inversionista'].includes(userInfo.role) &&
          userInfo.venues?.length > 0
        ) {
          const venuesQuery = query(
            venuesCollection,
            where('__name__', 'in', userInfo.venues)
          );
          const querySnapshot = await getDocs(venuesQuery);
          venuesData = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        }

        if (venuesData.length === 1) {
          navigate(`/dashboard/venues/${venuesData[0].id}`, { replace: true });
          return;
        }

        setVenues(venuesData);
      } catch (err) {
        console.error('Error fetching venues:', err);
        if (
          ['owner', 'viewer', 'inversionista'].includes(userInfo.role) &&
          (!userInfo.venues || userInfo.venues.length === 0)
        ) {
          setError('No tienes venues asignados.');
        } else {
          setError('Error al cargar los venues. Por favor, intenta de nuevo.');
        }
      } finally {
        setLoading(false);
      }
    };

    // Solo admins: los no-admin se redirigen a la ficha de su venue antes
    // de renderizar la lista, así que su fetch sería lectura desperdiciada.
    if (userInfo && isAdmin) {
      fetchVenues();
    }
  }, [userInfo, isAdmin, navigate]);

  const handleDelete = (venueId) => {
    setVenues((prev) => prev.filter((venue) => venue.id !== venueId));
  };

  const enrichedVenues = useMemo(
    () => venues.map((v) => ({ ...v, ...parseBasicInfo(v.basicInfo) })),
    [venues]
  );

  const zonaOptions = useMemo(() => {
    const set = new Set();
    enrichedVenues.forEach((v) => v.zone && set.add(v.zone));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [enrichedVenues]);

  const tipoOptions = useMemo(() => {
    const set = new Set();
    enrichedVenues.forEach((v) => typesAsArray(v.type).forEach((t) => t && set.add(t)));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [enrichedVenues]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enrichedVenues.filter((v) => {
      if (q) {
        const hay = [v.name, v.address].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filterZona && v.zone !== filterZona) return false;
      if (filterTipo && !typesAsArray(v.type).includes(filterTipo)) return false;
      if (filterPublic === 'activo' && !v.isActive) return false;
      if (filterPublic === 'inactivo' && v.isActive) return false;
      if (filterAdmin === 'activo' && !v.isActiveAdmin) return false;
      if (filterAdmin === 'inactivo' && v.isActiveAdmin) return false;
      if (filterEn === 'pendiente' && !needsEnTranslation(v, knownDocLabels)) return false;
      if (filterEn === 'completo' && needsEnTranslation(v, knownDocLabels)) return false;
      return true;
    });
  }, [enrichedVenues, search, filterZona, filterTipo, filterPublic, filterAdmin, filterEn, knownDocLabels]);

  const sorted = useMemo(
    () => [...filtered].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es')),
    [filtered]
  );

  const hasActiveFilters =
    search || filterZona || filterTipo || filterPublic || filterAdmin || filterEn;

  const clearFilters = () => {
    setSearch('');
    setFilterZona('');
    setFilterTipo('');
    setFilterPublic('');
    setFilterAdmin('');
    setFilterEn('');
  };

  if (!isAdmin) {
    if (venueLoading) return <Loader />;
    if (selectedVenueId) {
      return <Navigate to={`/dashboard/venues/${selectedVenueId}`} replace />;
    }
    return (
      <div className="p-4 text-center text-red-600">
        No tienes venues asignados.
      </div>
    );
  }

  if (loading) return <Loader />;
  if (error) return <div className="p-4 text-center text-red-600">{error}</div>;

  const activeCount = venues.filter((v) => v.isActive).length;
  const adminActiveCount = venues.filter((v) => v.isActiveAdmin).length;
  const enPendingCount = venues.filter((v) => needsEnTranslation(v, knownDocLabels)).length;

  return (
    <div className="max-w-7xl mx-auto p-4 max-lg:px-0">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <Heading level={2}>Venues</Heading>
          <p className="text-sm text-zinc-500 mt-1">
            {isAdmin
              ? 'Directorio completo de venues en la plataforma.'
              : 'Tus venues en Venueboard.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={() => navigate('/dashboard/new-venue')}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 flex items-center gap-1.5"
              style={{ backgroundColor: ACCENT }}
            >
              <PlusIcon className="h-4 w-4" />
              Nuevo venue
            </button>
          )}
          <div className="inline-flex rounded-lg border border-zinc-200 bg-white overflow-hidden">
            <button
              onClick={() => setViewMode('table')}
              className={clsx(
                'p-2 transition-colors',
                viewMode === 'table'
                  ? 'text-white'
                  : 'text-zinc-500 hover:bg-zinc-50'
              )}
              style={viewMode === 'table' ? { backgroundColor: ACCENT } : undefined}
              aria-label="Vista de tabla"
              title="Vista de tabla"
            >
              <TableCellsIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={clsx(
                'p-2 transition-colors border-l border-zinc-200',
                viewMode === 'cards'
                  ? 'text-white'
                  : 'text-zinc-500 hover:bg-zinc-50'
              )}
              style={viewMode === 'cards' ? { backgroundColor: ACCENT } : undefined}
              aria-label="Vista de tarjetas"
              title="Vista de tarjetas"
            >
              <Squares2X2Icon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
        <div
          className={clsx(
            'grid grid-cols-1 sm:grid-cols-2 gap-3',
            isAdmin ? 'lg:grid-cols-6' : 'lg:grid-cols-4'
          )}
        >
          <div className="relative sm:col-span-2 lg:col-span-2">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre o dirección..."
              className="w-full rounded-lg border border-zinc-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
            />
          </div>
          <select
            value={filterZona}
            onChange={(e) => setFilterZona(e.target.value)}
            className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
          >
            <option value="">Todas las zonas</option>
            {zonaOptions.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
          <select
            value={filterTipo}
            onChange={(e) => setFilterTipo(e.target.value)}
            className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
          >
            <option value="">Todos los tipos</option>
            {tipoOptions.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <select
            value={filterPublic}
            onChange={(e) => setFilterPublic(e.target.value)}
            className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
          >
            <option value="">Público: todos</option>
            <option value="activo">Público: activos</option>
            <option value="inactivo">Público: inactivos</option>
          </select>
          {isAdmin && (
            <select
              value={filterAdmin}
              onChange={(e) => setFilterAdmin(e.target.value)}
              className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
            >
              <option value="">Admin: todos</option>
              <option value="activo">Admin: activos</option>
              <option value="inactivo">Admin: inactivos</option>
            </select>
          )}
          {isAdmin && (
            <select
              value={filterEn}
              onChange={(e) => setFilterEn(e.target.value)}
              className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
            >
              <option value="">EN: todos</option>
              <option value="pendiente">EN: pendientes</option>
              <option value="completo">EN: completos</option>
            </select>
          )}
        </div>

        {/* Summary row */}
        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
          <Badge color="zinc">
            {hasActiveFilters
              ? `${sorted.length} de ${venues.length}`
              : `Total: ${venues.length}`}
          </Badge>
          <Badge color="green">Público activos: {activeCount}</Badge>
          {isAdmin && <Badge color="green">Admin activos: {adminActiveCount}</Badge>}
          {isAdmin && enPendingCount > 0 && (
            <button
              type="button"
              onClick={() => setFilterEn('pendiente')}
              title="Ver solo venues con traducción al inglés pendiente"
            >
              <Badge color="amber">EN pendiente: {enPendingCount}</Badge>
            </button>
          )}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="ml-auto inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-700"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {sorted.length === 0 ? (
        <div className="rounded-xl border border-zinc-200 bg-white shadow-sm text-center py-16 px-4">
          <BuildingOffice2Icon className="h-10 w-10 mx-auto text-zinc-300 mb-3" />
          <div className="text-zinc-500">
            {venues.length === 0
              ? 'Aún no hay venues.'
              : 'Ningún venue coincide con los filtros.'}
          </div>
          {hasActiveFilters && venues.length > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              className="mt-3 text-sm text-[#1b4d2e] hover:underline"
            >
              Limpiar filtros
            </button>
          )}
        </div>
      ) : viewMode === 'cards' ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3">
          {sorted.map((venue) => (
            <VenueCard
              key={venue.id}
              venue={venue}
              onDelete={handleDelete}
              isAdmin={isAdmin}
              knownDocLabels={knownDocLabels}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-zinc-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-zinc-50">
                <tr className="border-b border-zinc-200">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 w-20"></th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                    Nombre
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                    Zona
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                    Tipo
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                    Capacidad
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                    Público
                  </th>
                  {isAdmin && (
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                      Admin
                    </th>
                  )}
                  {isAdmin && (
                    <th
                      className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500"
                      title="Traducción al inglés (descripción del venue y de su ubicación)"
                    >
                      EN
                    </th>
                  )}
                  {isAdmin && <th className="px-2 py-2.5 w-10"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {sorted.map((venue) => (
                  <VenueRow
                    key={venue.id}
                    venue={venue}
                    isAdmin={isAdmin}
                    onDelete={handleDelete}
                    knownDocLabels={knownDocLabels}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default VenuesList;
